import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Briefcase, Home, ChevronRight, Users, Wrench, Wallet, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLIENT_TYPES = [
  { value: "vendor", label: "Vendor (Selling)", icon: Home },
  { value: "buyer", label: "Buyer", icon: Users },
  { value: "landlord", label: "Landlord", icon: Briefcase },
  { value: "tenant", label: "Tenant", icon: Users },
];

const CONDITIONS = [
  { value: "good", label: "Good", desc: "Well-maintained, minimal work needed" },
  { value: "fair", label: "Fair", desc: "Average condition, some updates needed" },
  { value: "needs_work", label: "Needs Work", desc: "Significant renovation required" },
];

const BUDGETS = [
  { value: "none", label: "Not discussed" },
  { value: "under_20k", label: "Under £20k" },
  { value: "20k_to_50k", label: "£20k – £50k" },
  { value: "over_50k", label: "£50k+" },
];

export default function PartnerContext() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [clientType, setClientType] = useState("vendor");
  const [condition, setCondition] = useState("fair");
  const [budget, setBudget] = useState("none");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const diagnosticData = JSON.parse(sessionStorage.getItem(`diagnostic_data_${sessionId}`) || "{}");
      const sessionData = JSON.parse(sessionStorage.getItem(`partner_session_${sessionId}`) || "{}");
      
      if (!diagnosticData.dwelling_type) {
        toast.error("Diagnostic data not found. Please restart the assessment.");
        navigate("/partner");
        return;
      }
      
      const partnerContext = {
        client_type: clientType,
        condition: condition,
        budget_indication: budget,
        notes: notes.trim() || null,
      };
      
      const payload = {
        first_name: sessionData.first_name || "Client",
        postcode: sessionData.postcode || diagnosticData.postcode,
        property_era: diagnosticData.property_era || "classic",
        dwelling_type: diagnosticData.dwelling_type,
        bedrooms: diagnosticData.bedrooms,
        bathrooms: parseInt(diagnosticData.bathrooms) || 1,
        separate_kitchen: diagnosticData.separate_kitchen,
        wide_hallways_over_1m: diagnosticData.wide_hallways_over_1m,
        layout_changed_since_built: diagnosticData.layout_changed_since_built,
        features: diagnosticData.features || [],
        epc_band: diagnosticData.epc_band,
        main_goal: diagnosticData.main_goal,
        email: sessionData.email || "partner@cipher.app",
        consent: true,
        is_partner: true,
        partner_context: partnerContext,
      };

      const response = await axios.post(`${API}/hippo/teaser`, payload);
      const submissionId = response.headers["x-submission-id"];
      const accessToken = response.headers["x-access-token"];
      
      if (!submissionId) throw new Error("No submission ID returned");
      
      sessionStorage.setItem(`hippo_data_${submissionId}`, JSON.stringify(response.data));
      sessionStorage.setItem(`hippo_token_${submissionId}`, accessToken);
      sessionStorage.setItem(`hippo_session_${submissionId}`, JSON.stringify({
        first_name: sessionData.first_name || "Client",
        postcode: sessionData.postcode || diagnosticData.postcode,
        dwelling_type: diagnosticData.dwelling_type,
        bedrooms: diagnosticData.bedrooms,
        features: diagnosticData.features || [],
        separate_kitchen: diagnosticData.separate_kitchen,
        main_goal: diagnosticData.main_goal,
      }));
      sessionStorage.setItem(`partner_context_${submissionId}`, JSON.stringify(partnerContext));
      
      navigate(`/hippo/${submissionId}`);
    } catch (error) {
      console.error("Partner submission error:", error);
      toast.error("Failed to generate insight. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a0a] flex flex-col overflow-auto">
      <header className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <div className="w-8 h-8 rounded-lg bg-cipher-green/20 flex items-center justify-center">
            <Briefcase className="w-4 h-4 text-cipher-green" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">Partner Lens</p>
            <p className="text-[10px] text-white/40">Additional context for professional assessment</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 max-w-lg mx-auto w-full">
        {/* Client Relationship */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
            Client Relationship
          </label>
          <div className="grid grid-cols-2 gap-2">
            {CLIENT_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => setClientType(type.value)}
                className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all text-left ${
                  clientType === type.value
                    ? "bg-cipher-green/15 border-cipher-green/40 text-white"
                    : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"
                }`}
                data-testid={`client-type-${type.value}`}
              >
                <type.icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Property Condition */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
            Property Condition
          </label>
          <div className="space-y-2">
            {CONDITIONS.map((cond) => (
              <button
                key={cond.value}
                onClick={() => setCondition(cond.value)}
                className={`w-full flex items-center justify-between p-3.5 rounded-xl border transition-all text-left ${
                  condition === cond.value
                    ? "bg-cipher-green/15 border-cipher-green/40"
                    : "bg-white/5 border-white/10 hover:bg-white/10"
                }`}
                data-testid={`condition-${cond.value}`}
              >
                <div>
                  <p className={`text-sm font-medium ${condition === cond.value ? "text-white" : "text-white/70"}`}>
                    {cond.label}
                  </p>
                  <p className="text-[11px] text-white/40 mt-0.5">{cond.desc}</p>
                </div>
                {condition === cond.value && (
                  <div className="w-5 h-5 rounded-full bg-cipher-green/30 flex items-center justify-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-cipher-green" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Budget Indication */}
        <div className="mb-6">
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
            <Wallet className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Budget Indication
          </label>
          <div className="flex flex-wrap gap-2">
            {BUDGETS.map((b) => (
              <button
                key={b.value}
                onClick={() => setBudget(b.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  budget === b.value
                    ? "bg-cipher-green/15 border-cipher-green/40 text-white"
                    : "bg-white/5 border-white/10 text-white/50 hover:bg-white/10"
                }`}
                data-testid={`budget-${b.value}`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>

        {/* Agent Notes */}
        <div className="mb-8">
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
            <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Notes <span className="text-white/30 font-normal">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any context for this assessment..."
            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/30 resize-none h-20 focus:outline-none focus:border-cipher-green/40 transition-colors"
            data-testid="partner-notes"
          />
        </div>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-cipher-green hover:bg-cipher-green/90 text-white py-4 text-base font-medium rounded-xl shadow-lg shadow-cipher-green/20 transition-all"
          data-testid="partner-generate-btn"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Generating...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              Generate Insight
              <ChevronRight className="w-4 h-4" />
            </span>
          )}
        </Button>
        
        <p className="text-[10px] text-white/30 text-center mt-4">
          Partner assessments are auto-unlocked. No client payment required.
        </p>
      </main>
    </div>
  );
}
